import axios from "axios"
import { useFormik } from "formik"
import PropTypes from "prop-types"
import React, { useEffect, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import {
  Card,
  CardBody,
  Col,
  Container,
  FormFeedback,
  Input,
  Label,
  Row
} from "reactstrap"
import * as Yup from "yup"
import "./main.scss"

import Breadcrumbs from "components/Common/BreadcrumbTitle"

import {
  addSystemConfiguration,
  getSystemConfigurationById,
  editSystemConfiguration,
  downloadFile,
  downloadSystemConfigFile as downloadSystemFile,
} from "../../../http/UAM/systemConfig.helper";

import { API_URL } from "../../../http/url.helper"
import CommonDialog from "components/Common/CommonDialog"
import { useToast } from "ToastStore"
import { decryptedValue } from "common/utility"

const AddSystemConfiguration = props => {
  const navigate = useNavigate()

  const location = useLocation()
  const pathnameSegments = location.pathname.split("/")
  const lastSegment = pathnameSegments[pathnameSegments.length - 1]

  document.title = `${lastSegment.toUpperCase()} System Configuration | FundLync`

  const urlParams = new URLSearchParams(window.location.search)
  const encodedId = urlParams.get("id")
  const decodedId = atob(encodedId)

  const [showCommonDialog, setShowCommonDialog] = useState({ isShow: false });
  const [systemConfigList, setSystemConfigList] = useState([])
  const [showDropZone, setShowDropZone] = useState(false)
  const [showconfigValueAsInput, setShowconfigValueAsInput] = useState(true)
  const [showViewTemplate, setShowViewTemplate] = useState(false)
  const [showEditTemplate, setShowEditTemplate] = useState(true)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [errorMessage, setErrorMessage] = useState(null)
  const [filePath, setFilePath] = useState(null)
  const [fileSize, setFileSize] = useState(null)
  const [showDownloadForEdit, setShowDownloadForEdit] = useState(false)

  const { handleToastData } = useToast();

  const user = decryptedValue(localStorage.getItem("user"));

  useEffect(() => {
    if (lastSegment == "view" || lastSegment == "edit") {
      getSystemConfigurationById({ sysConfigId: decodedId })
        .then(responseFromAPI => {
          if (responseFromAPI.status && responseFromAPI.code == 200) {
            let responseData = responseFromAPI.data

            if (responseData.configType == "file") {
              setShowconfigValueAsInput(false)
              setShowDropZone(true)
            }
            setSystemConfigList(responseData)
          }
        })
    }
    if (lastSegment == "view") {
      setShowViewTemplate(true)
      setShowEditTemplate(false)
    } else if (lastSegment == "edit") {
      setShowDownloadForEdit(true)
    } else {
      setShowViewTemplate(false)
      setShowEditTemplate(true)
      setShowDownloadForEdit(false)
    }
  }, [])

  // validation
  const validation = useFormik({
    // enableReinitialize : use this flag when initial values needs to be changed
    enableReinitialize: true,

    initialValues: {
      configKey: systemConfigList.configKey || "",
      configDescription: systemConfigList.configDescription || "",
      configValue: systemConfigList.configValue || "",
      configType: systemConfigList.configType || "",
    },
    validationSchema: Yup.object({
      configKey: Yup.string()
        .required("Config Key is Required")
        .test(
          "no-space",
          "Config Key cannot contain spaces",
          value => !/\s/.test(value)
        ),
      configDescription: Yup.string().required(
        "Config Description is Required"
      ),
      configType: Yup.string().required("Config Type is Required"),
      configValue: Yup.string().when("showconfigValueAsInput", {
        is: true,
        then: Yup.string().required("Config Value is Required"),
        otherwise: Yup.string(),
      }),
    }),
    onSubmit: values => {
      const message = lastSegment == 'edit' ? 'Are you sure want to update System Configuration?' : 'Are you sure want to add System Configuration?'
      setShowCommonDialog({ isShow: true, message: message })


    },
  })

  const navigateBack = () => {
    const user = decryptedValue(localStorage.getItem("user"));
    navigate(`/${user.roleName}/system-config`)
  }

  const onFileChange = async e => {
    const inputElement = e.target
    if (inputElement.files && inputElement.files.length > 0) {
      setFilePath(inputElement.files[0]?.name)
      setFileSize(inputElement.files[0]?.size)
      const selectedFile = inputElement.files[0]
      if (selectedFile) {
        let formData = new FormData()
        formData.append("upl", selectedFile)
        const config = {
          onUploadProgress: progressEvent => {
            const percentage = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            )
            setUploadProgress(percentage)
          },
        }
        try {
          setErrorMessage(null)
          const response = await axios.post(
            `${API_URL}/s3/uploadSystemConfigFiles`,
            formData,
            config
          )
          if (response.status == 200) {
            setErrorMessage(null)
            setFilePath(response.data[0].fileName)
          } else {
            setErrorMessage(response.data.message)
          }
        } catch (error) {
          setErrorMessage(error.response.data.message)
          console.error("Error uploading file:", error)
        }
      }
      //  dispatch(uploadFile(selectedFile))
    }
  }


  const downloadSystemConfigFile = file => {
    const str = file.substring(file.indexOf("com") + 4);
    downloadSystemFile({ fileName: str })
      .then(responseFromAPI => {
        if (responseFromAPI.status && responseFromAPI.code == 200) {
          const { result } = responseFromAPI;
          downloadFile(result, ext, filename);
        }
      })
      .catch(error => {
        console.log(error)
      })
  }

  function formatBytes(bytes, decimals = 2) {
    if (bytes === 0) return "0 Bytes"
    const k = 1024
    const dm = decimals < 0 ? 0 : decimals
    const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"]

    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i]
  }

  const submitSystemConfig = () => {
    const { values } = validation
    if (lastSegment == "edit") {
      const updateTemplate = {
        id: decodedId,
        configKey: values.configKey,
        configDescription: values.configDescription,
        configValue: filePath || values.configValue,
        configType: values.configType,
      }
      editSystemConfiguration(updateTemplate)
        .then(responseFromAPI => {
          if (responseFromAPI.status && responseFromAPI.code == 201) {
            handleToastData(responseFromAPI.message, "success")
            navigate(`/${user.roleName}/system-config`);
          }
        })
        .catch(error => { })
      validation.resetForm()
    } else {
      const newTemplate = {
        configKey: values.configKey,
        configDescription: values.configDescription,
        configValue: values.configValue || filePath,
        configType: values.configType,
        category: "application",
      }
      addSystemConfiguration(newTemplate)
        .then(responseFromAPI => {
          if (responseFromAPI.code == 201) {
            handleToastData(responseFromAPI.message, "success")
            navigate(`/${user.roleName}/system-config`);
          }
        })
        .catch(error => { })
      validation.resetForm()
      setUploadProgress(0)
    }
  }

  const dialogAction = (action, data) => {
    if (action == 1) {
      setShowCommonDialog({ isShow: false })
      submitSystemConfig()

    } else {
      setShowCommonDialog({ isShow: false })
    }
  }


  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="SYSTEM CONFIGURATION" />
          <Row className="vertical-scroll">
            <Col xs="12">
              <div>
                <div>
                  {showEditTemplate && (
                    <form
                      onSubmit={e => {
                        e.preventDefault()
                        validation.handleSubmit()
                        return false
                      }}
                    >
                      <Row className="mb-4">
                        <Col sm={6}>
                          <Label
                            htmlFor="horizontal-email-Input"
                            className="col-sm-3 col-form-label"
                          >
                            Config Key *
                          </Label>
                          <Input
                            type="text"
                            className="form-control"
                            name="configKey"
                            id="horizontal-url-Input"
                            placeholder="Enter Config Key"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.configKey || ""}
                            invalid={
                              validation.touched.configKey && validation.errors.configKey
                            }
                          />
                          {validation.touched.configKey &&
                            validation.errors.configKey ? (
                            <FormFeedback type="invalid">
                              {validation.errors.configKey}
                            </FormFeedback>
                          ) : null}
                        </Col>
                        <Col sm={6}>
                          <Label
                            htmlFor="horizontal-notification-Input"
                            className="col-sm-3 col-form-label"
                          >
                            Config Type *
                          </Label>
                          <Input
                            name="configType"
                            type="select"
                            onChange={e => {
                              if (e.target.value == "file") {
                                setShowDropZone(true)
                                setShowconfigValueAsInput(false)
                              } else {
                                setShowconfigValueAsInput(true)
                                setShowDropZone(false)
                              }
                              validation.handleChange(e)
                            }}
                            onBlur={validation.handleBlur}
                            value={validation.values.configType || ""}
                            invalid={
                              validation.touched.configType &&
                              validation.errors.configType
                            }
                          >
                            <option value="" disabled>
                              Select
                            </option>
                            <option value="data">Data</option>
                            <option value="file">File</option>
                          </Input>
                          {validation.touched.configType &&
                            validation.errors.configType ? (
                            <FormFeedback type="invalid">
                              {validation.errors.configType}
                            </FormFeedback>
                          ) : null}
                        </Col>
                      </Row>
                      <Row className="mb-4">
                        <Col sm={6}>
                          <Label
                            htmlFor="horizontal-email-Input"
                            className="col-sm-3 col-form-label"
                          >
                            Description *
                          </Label>
                          <Input
                            type="text"
                            className="form-control"
                            name="configDescription"
                            id="horizontal-url-Input"
                            placeholder="Enter Config Description"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.configDescription || ""}
                            invalid={
                              validation.touched.configDescription &&
                              validation.errors.configDescription
                            }
                          />
                          {validation.touched.configDescription &&
                            validation.errors.configDescription ? (
                            <FormFeedback type="invalid">
                              {validation.errors.configDescription}
                            </FormFeedback>
                          ) : null}
                        </Col>
                        {showconfigValueAsInput && (
                          <Col sm={6}>
                            <Label
                              htmlFor="horizontal-notification-Input"
                              className="col-sm-3 col-form-label"
                            >
                              Config Value *
                            </Label>
                            <Input
                              type="textarea"
                              className="form-control"
                              name="configValue"
                              id="horizontal-email-Input"
                              placeholder="Enter Config Value"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              value={validation.values.configValue || ""}
                              invalid={
                                validation.touched.configValue &&
                                validation.errors.configValue
                              }
                            />
                            {validation.touched.configValue &&
                              validation.errors.configValue ? (
                              <FormFeedback type="invalid">
                                {validation.errors.configValue}
                              </FormFeedback>
                            ) : null}
                          </Col>
                        )}
                      </Row>
                      <Row>
                        {showDropZone && (
                          <Col sm={6}>
                            <div>
                              <div>
                                <Label
                                  htmlFor="horizontal-email-Input"
                                  className="col-sm-6 col-form-label"
                                >
                                  Upload File (.xlsx, .xls, .jpg, .png, .jpeg,
                                  .pdf ) *
                                </Label>
                                <div className="drop">
                                  <div className="text-wrapper">
                                    <div className="centered">
                                      <input
                                        type="file"
                                        name="formatDocument"
                                        id="fileExcel"
                                        accept=".jpg, .png, .jpeg, .pdf, .xlsx, .xls"
                                        onChange={onFileChange}
                                      />
                                      <label
                                        style={{
                                          marginBottom: 0,
                                          fontSize: "14px",
                                        }}
                                        htmlFor="fileExcel"
                                      >
                                        <img
                                          src="https://scfuat.cashinvoice.in/frontend/assets/themes/image/icon/cloud.png"
                                          className="img-fluid"
                                          style={{ width: "30px" }}
                                          alt="Upload"
                                        />
                                        <span className="textLink">
                                          Browse files to attach
                                        </span>
                                        or Drop
                                      </label>
                                    </div>
                                  </div>
                                </div>
                                <div className="mt-3 mb-1 d-inline-block">
                                  <span style={{ fontSize: "12px", color: "#535C68" }}>{filePath?filePath:''}</span>
                                  {fileSize && <span className="ps-2 fw-bolder " style={{ fontSize: "10px", color: "#99AAAB"}}>({formatBytes(fileSize)})</span>}
                                  </div>
                                {uploadProgress > 0 && !errorMessage && (
                                <div className="progress">
                                    <div
                                      className="progress-bar bg-success"
                                      role="progressbar"
                                      style={{ width: `${uploadProgress}%` }}
                                      aria-valuenow="0"
                                      aria-valuemin="0"
                                      aria-valuemax="100"
                                    >
                                      {`${uploadProgress}%`}
                                    </div>
                                  </div>
                                )}
                                {errorMessage && (
                                  <div className="alert alert-danger mt-2">
                                    {errorMessage}
                                  </div>
                                )}
                              </div>
                              <hr />
                            </div>
                            
                          </Col>
                        )}
                        {showDownloadForEdit && (
                          <div>

                            {systemConfigList.configType == "file" ? (
                              <div className="d-flex">
                                <div className="mt-1"> {systemConfigList.configValue}</div>
                                <i
                                  className="mdi mdi-download downloadFile"
                                  style={{ marginLeft: "20px" }}
                                  onClick={() => {
                                    downloadSystemConfigFile(
                                      systemConfigList.configValue
                                    )
                                  }}
                                ></i>

                              </div>
                            ) : (
                              ""
                            )}
                          </div>
                        )}
                      </Row>
                      <Row>
                        <Col md={12}>
                          <div
                            className="text-end"
                            // style={{ margin: "20px 17% 0px 0px" }}
                          >
                            <button
                              className="btn btn-outline-success save-customer me-3"
                              onClick={navigateBack}
                            >
                              Back
                            </button>
                            <button
                              type="submit"
                              className="btn btn-success save-customer"
                            >
                              Save
                            </button>
                          </div>
                        </Col>
                      </Row>
                    </form>
                  )}
                  {showViewTemplate && (
                    <div>
                      <Row className="mb-4">
                        <Col sm={5}>
                          <Label className="col-sm-3 col-form-label label">
                            Config Key *
                          </Label>
                          <p className="fs-5">
                            {systemConfigList.configKey || "-"}
                          </p>
                        </Col>
                        <Col sm={5}>
                          <Label
                            htmlFor="horizontal-notification-Input"
                            className="col-sm-3 col-form-label label"
                          >
                            Config Type *
                          </Label>
                          <p className="fs-5">
                            {systemConfigList.configType || "-"}
                          </p>
                        </Col>
                      </Row>
                      <Row className="mb-4">
                        <Col sm={5}>
                          <Label className="col-sm-3 col-form-label label">
                            Description *
                          </Label>
                          <p className="fs-5">
                            {systemConfigList.configDescription || "-"}
                          </p>
                        </Col>
                        <Col sm={5}>
                          <Label
                            htmlFor="horizontal-notification-Input"
                            className="col-sm-3 col-form-label label"
                          >
                            Config value *
                          </Label>
                          <div>
                            <p className="fs-5">
                              {systemConfigList.configValue || "-"}
                            </p>
                            <div>
                              {systemConfigList.configType == "file" ? (
                                <i
                                  className="mdi mdi-download downloadFile"
                                  onClick={() => {
                                    downloadSystemConfigFile(
                                      systemConfigList.configValue
                                    )
                                  }}
                                ></i>
                              ) : null
                              }
                            </div>
                          </div>
                        </Col>
                      </Row>
                      <Row className="mb-4">
                        <Col sm={5}>
                          <Label className="col-sm-3 col-form-label label">
                            Category
                          </Label>
                          <p className="fs-5">
                            {systemConfigList.category || "-"}
                          </p>
                        </Col>
                      </Row>
                      <Row>
                        <Col>
                          <div>
                            <button
                              className="btn btn-outline-success"
                              onClick={() => {
                                navigateBack()
                              }}
                            >
                              Back
                            </button>
                          </div>
                        </Col>
                      </Row>
                    </div>
                  )}
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
      {showCommonDialog &&
        <CommonDialog isShow={showCommonDialog.isShow} message={showCommonDialog.message} data={showCommonDialog.data} postitiveBtn={'Submit'} negativeBtn={'Close'} onCloseFn={dialogAction}></CommonDialog>
      }
    </React.Fragment>
  )
}

AddSystemConfiguration.propTypes = {
  history: PropTypes.object,
}

export default AddSystemConfiguration
